import { useEffect, useState, useMemo } from "react";
import { api, API_BASE } from "@/api/client";
import { PageHeader, fmtDate } from "@/components/common";
import { Download, Loader2, ChevronUp, ChevronDown, ChevronsUpDown } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";

const REPORTS = [
  { key: "team360", label: "SKIVERSE Executive" },
  { key: "individual", label: "Individual Performance" },
  { key: "team", label: "Team Performance" },
  { key: "department", label: "Department Performance" },
  { key: "sla-breach", label: "SLA Breach" },
  { key: "approval-aging", label: "Approval Aging" },
  { key: "return-confirmation", label: "Return Confirmation" },
  { key: "recurring-compliance", label: "Recurring Compliance" },
  { key: "whatsapp", label: "WhatsApp Usage" },
];

export default function Reports() {
  const [active, setActive] = useState("team360");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/reports/${active}`);
      setData(r.data?.data || {});
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [active]);

  const exportCsv = (report) => {
    const token = localStorage.getItem("t360_access_token");
    const url = `${API_BASE}/reports/export?report=${report}`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.download = `${report}.csv`;
        link.click();
      });
  };

  return (
    <div data-testid="reports-page">
      <PageHeader title="SKIVERSE Reports" breadcrumbs={["Reports", "Dashboard"]}
        actions={
          <div className="flex gap-2">
            <button onClick={() => exportCsv("tasks")} className="erp-btn-secondary inline-flex items-center gap-2" data-testid="export-tasks-btn"><Download size={14}/> Export Tasks</button>
            <button onClick={() => exportCsv("audit")} className="erp-btn-secondary inline-flex items-center gap-2"><Download size={14}/> Export Audit</button>
          </div>
        }
      />

      <div className="border-b border-slate-200 mb-4 overflow-x-auto">
        <div className="flex space-x-6 text-sm font-medium">
          {REPORTS.map(r => (
            <button key={r.key} onClick={() => setActive(r.key)}
              className={`pb-2 whitespace-nowrap ${active === r.key ? "text-slate-900 border-b-2 border-slate-900" : "text-slate-500 hover:text-slate-800"}`}
              data-testid={`report-tab-${r.key}`}
            >{r.label}</button>
          ))}
        </div>
      </div>

      {loading ? <div className="p-12 text-center"><Loader2 className="animate-spin inline"/></div> :
       !data ? <div className="text-slate-500">No data</div> :
       <ReportRender type={active} data={data} />
      }
    </div>
  );
}

function ReportRender({ type, data }) {
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");

  // Reset sort when switching report type
  useEffect(() => { setSortKey(null); setSortDir("asc"); }, [type]);

  const sortRows = (rows) => {
    if (!sortKey) return rows;
    return [...rows].sort((a, b) => {
      const va = a[sortKey] ?? "", vb = b[sortKey] ?? "";
      const cmp = typeof va === "number" ? va - vb : String(va).localeCompare(String(vb));
      return sortDir === "asc" ? cmp : -cmp;
    });
  };

  const toggle = (key) => {
    if (sortKey === key) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("asc"); }
  };

  const Th = ({ label, k }) => (
    <th onClick={() => toggle(k)} className="cursor-pointer select-none group">
      <div className="flex items-center gap-1.5">{label}
        {sortKey === k ? (sortDir === "asc" ? <ChevronUp size={11} className="text-[#334E7B]"/> : <ChevronDown size={11} className="text-[#334E7B]"/>)
          : <ChevronsUpDown size={11} className="opacity-0 group-hover:opacity-50 transition-opacity"/>}
      </div>
    </th>
  );
  if (type === "team360") {
    const cards = [
      ["Total Tasks", data.total_tasks],
      ["Open", data.open_tasks],
      ["Closed", data.closed_tasks],
      ["Overdue", data.overdue_tasks, "text-red-700"],
      ["Approval Pending", data.approval_pending, "text-violet-700"],
      ["Return Pending", data.return_pending, "text-yellow-700"],
      ["On-Time %", `${data.on_time_percentage || 0}%`, "text-emerald-700"],
      ["Delay %", `${data.delay_percentage || 0}%`, "text-red-700"],
    ];
    const chartData = Object.entries(data.by_status || {}).map(([k, v]) => ({ status: k, count: v }));
    return (
      <>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {cards.map(([l, v, c], i) => (
            <div key={i} className="erp-card p-4">
              <div className="text-[11px] uppercase tracking-wider text-slate-500">{l}</div>
              <div className={`text-3xl font-heading font-bold mt-1 ${c || "text-slate-900"}`}>{v ?? 0}</div>
            </div>
          ))}
        </div>
        <div className="card">
          <div className="card-head"><h3><span className="dot" />Tasks by Status</h3></div>
          <div className="card-body h-80">
          <ResponsiveContainer>
            <BarChart data={chartData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
              <CartesianGrid stroke="#D7DEEC" strokeDasharray="3 3" />
              <XAxis dataKey="status" tick={{ fontSize: 9, fill: "#64748B" }} />
              <YAxis tick={{ fontSize: 10, fill: "#64748B" }} allowDecimals={false} />
              <Tooltip contentStyle={{ borderRadius: 2, fontSize: 12 }} />
              <Bar dataKey="count" fill="#283F62" />
            </BarChart>
          </ResponsiveContainer>
          </div>
        </div>
      </>
    );
  }

  if (type === "individual") {
    const s = data.stats || {};
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="erp-card p-5">
          <div className="text-xs uppercase tracking-wider text-slate-500">User</div>
          <div className="font-heading text-xl font-bold">{data.user?.name || "—"}</div>
          <div className="text-sm text-slate-500">{data.user?.email}</div>
          <div className="mt-4 text-[11px] uppercase tracking-wider text-slate-500">Performance Score</div>
          <div className="text-5xl font-heading font-bold text-emerald-700">{s.performance_score || 0}<span className="text-lg text-slate-400">/100</span></div>
        </div>
        <div className="erp-card p-5">
          <div className="grid grid-cols-2 gap-3">
            {Object.entries(s).filter(([k]) => k !== "performance_score").map(([k, v]) => (
              <div key={k} className="text-sm border border-slate-200 rounded-sm p-2">
                <div className="text-[10px] uppercase tracking-wider text-slate-500">{k.replace(/_/g, " ")}</div>
                <div className="font-heading font-bold text-lg text-slate-900">{v}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (type === "team" || type === "department") {
    const nameKey = type === "team" ? "team_name" : "department_name";
    const sorted = sortRows(data.rows || []);
    return (
      <div className="erp-card overflow-x-auto">
        <table className="erp-table">
          <thead><tr>
            <Th label={type === "team" ? "Team" : "Department"} k={nameKey}/>
            {type === "team" && <Th label="Members" k="members"/>}
            <Th label="Total" k="total"/>
            <Th label="Completed" k="completed"/>
            <Th label="Delayed" k="delayed"/>
            {type === "department" && <Th label="Approval Pending" k="approval_pending"/>}
            <Th label="Completion %" k="completion_rate"/>
          </tr></thead>
          <tbody>
            {sorted.map((r, i) => (
              <tr key={i}>
                <td className="font-medium">{r[nameKey]}</td>
                {type === "team" && <td>{r.members}</td>}
                <td>{r.total}</td>
                <td className="text-emerald-700">{r.completed}</td>
                <td className="text-red-700">{r.delayed}</td>
                {type === "department" && <td className="text-violet-700">{r.approval_pending}</td>}
                <td><span className="badge-base bg-blue-50 text-blue-700 border-blue-200">{r.completion_rate}%</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (type === "sla-breach" || type === "approval-aging" || type === "return-confirmation") {
    const sorted = sortRows(data.rows || []);
    return (
      <div className="erp-card overflow-x-auto">
        <table className="erp-table">
          <thead>
            <tr>
              <Th label="Code" k="task_code"/>
              <Th label="Title" k="title"/>
              <Th label="Assignee" k="assigned_to_name"/>
              <Th label="Status" k="status"/>
              <Th label="SLA Due" k="sla_due_date"/>
              {type === "approval-aging" && <Th label="Aging (hrs)" k="aging_hours"/>}
              {type === "sla-breach" && <Th label="Delay (hrs)" k="delay_hours"/>}
            </tr>
          </thead>
          <tbody>
            {sorted.map(r => (
              <tr key={r.id}>
                <td className="font-mono text-xs">{r.task_code}</td>
                <td className="font-medium">{r.title}</td>
                <td>{r.assigned_to_name}</td>
                <td>{r.status}</td>
                <td className="text-xs">{fmtDate(r.sla_due_date)}</td>
                {type === "approval-aging" && <td className="text-orange-700 font-bold">{r.aging_hours}</td>}
                {type === "sla-breach" && <td className="text-red-700 font-bold">{r.delay_hours}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (type === "recurring-compliance") {
    const sorted = sortRows(data.rows || []);
    return (
      <div className="erp-card overflow-x-auto">
        <table className="erp-table">
          <thead><tr>
            <Th label="Title" k="title"/>
            <Th label="Type" k="recurrence_type"/>
            <Th label="Generated" k="generated_tasks"/>
            <Th label="Completed" k="completed"/>
            <Th label="Delayed" k="delayed"/>
            <Th label="Compliance %" k="compliance_pct"/>
          </tr></thead>
          <tbody>
            {sorted.map(r => (
              <tr key={r.id}>
                <td className="font-medium">{r.title}</td>
                <td>{r.recurrence_type}</td>
                <td>{r.generated_tasks}</td>
                <td className="text-emerald-700">{r.completed}</td>
                <td className="text-red-700">{r.delayed}</td>
                <td><span className="badge-base bg-blue-50 text-blue-700 border-blue-200">{r.compliance_pct}%</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (type === "whatsapp") {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[["Total", data.total],["Sent", data.sent, "text-emerald-700"],["Received", data.received, "text-[#334E7B]"],["Failed", data.failed, "text-red-700"]].map(([l,v,c],i) => (
          <div key={i} className="erp-card p-4">
            <div className="text-[11px] uppercase tracking-wider text-slate-500">{l}</div>
            <div className={`text-3xl font-heading font-bold mt-1 ${c || "text-slate-900"}`}>{v}</div>
          </div>
        ))}
      </div>
    );
  }

  return <pre className="erp-card p-4 text-xs font-mono overflow-x-auto">{JSON.stringify(data, null, 2)}</pre>;
}
